import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import { ArrowRight, CheckCircle2, Zap, Shield, Clock, Euro, Plug, Battery, Car, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const wbTranslations = {
  de: {
    heroTitle: 'Wallbox',
    heroTitle2: '& Ladeinfrastruktur',
    heroDesc: 'Laden Sie Ihr Elektrofahrzeug bequem und schnell zu Hause. Mit unseren intelligenten Wallbox-Lösungen sind Sie bereit für die Mobilität der Zukunft.',
    freeConsult: 'Kostenlose Beratung',
    products: 'Produkte',
    quickCharging: 'Schnelles Laden',
    subsidy: 'Förderbar',
    smartConnect: 'Smart Connect',
    ourProducts: 'Unsere Wallbox-Produkte',
    productsDesc: 'Hochwertige Ladestationen für jeden Bedarf',
    benefits: 'Ihre Vorteile',
    benefitsDesc: 'Warum eine eigene Wallbox die beste Wahl ist',
    convenience: 'Komfort',
    convenienceDesc: 'Laden Sie Ihr E-Auto bequem über Nacht zu Hause',
    savings: 'Ersparnis',
    savingsDesc: 'Bis zu 50% günstiger als öffentliche Ladestationen',
    fastCharge: 'Schnellladung',
    fastChargeDesc: 'Bis zu 10x schneller als an der Haushaltssteckdose',
    smart: 'Intelligent',
    smartDesc: 'App-Steuerung und Solarstrom-Integration möglich',
    ready: 'Bereit für Ihre Wallbox?',
    readyDesc: 'Kontaktieren Sie uns für eine kostenlose Beratung. Wir finden die perfekte Ladelösung für Sie.'
  },
  en: {
    heroTitle: 'Wallbox',
    heroTitle2: '& Charging Infrastructure',
    heroDesc: 'Charge your electric vehicle conveniently and quickly at home. With our intelligent wallbox solutions, you are ready for the mobility of the future.',
    freeConsult: 'Free Consultation',
    products: 'Products',
    quickCharging: 'Quick Charging',
    subsidy: 'Eligible for Subsidy',
    smartConnect: 'Smart Connect',
    ourProducts: 'Our Wallbox Products',
    productsDesc: 'High-quality charging stations for every need',
    benefits: 'Your Benefits',
    benefitsDesc: 'Why your own wallbox is the best choice',
    convenience: 'Convenience',
    convenienceDesc: 'Charge your e-car conveniently overnight at home',
    savings: 'Savings',
    savingsDesc: 'Up to 50% cheaper than public charging stations',
    fastCharge: 'Fast Charging',
    fastChargeDesc: 'Up to 10x faster than a household socket',
    smart: 'Intelligent',
    smartDesc: 'App control and solar power integration possible',
    ready: 'Ready for Your Wallbox?',
    readyDesc: 'Contact us for a free consultation. We will find the perfect charging solution for you.'
  }
};

const WallboxHero = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = wbTranslations[language] || wbTranslations.de;

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/he50k9z3_Wallboximmagine%20moderna.jpeg"
          alt="Wallbox Ladestation"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {t.heroTitle}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-green-400">
                {t.heroTitle2}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
              {t.heroDesc}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                onClick={() => navigate('/beratung')}
                size="lg"
                className="bg-gradient-to-r from-sky-500 to-green-500 hover:from-sky-600 hover:to-green-600 text-white px-8 py-6 text-lg group shadow-xl"
              >
                {t.freeConsult}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => document.getElementById('produkte').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                {t.products}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">22kW</div>
                <div className="text-sm text-gray-400">{t.quickCharging}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">900€</div>
                <div className="text-sm text-gray-400">{t.subsidy}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">WiFi</div>
                <div className="text-sm text-gray-400">{t.smartConnect}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/70 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

const WallboxVorteile = () => {
  const { language } = useLanguage();
  
  const vorteile = language === 'en' ? [
    {
      icon: Zap,
      title: 'Fast Charging',
      description: 'Up to 10x faster than a regular socket - fully charged overnight'
    },
    {
      icon: Shield,
      title: 'Safe & Reliable',
      description: 'Integrated safety features and overload protection for worry-free charging'
    },
    {
      icon: Euro,
      title: 'Cost Savings',
      description: 'Cheap night electricity and up to €900 KfW subsidy possible'
    },
    {
      icon: Clock,
      title: 'Smart Charging',
      description: 'Time-controlled charging and benefit from cheap electricity tariffs'
    },
    {
      icon: Home,
      title: 'Home Comfort',
      description: 'Charge conveniently in your own garage or carport'
    },
    {
      icon: Battery,
      title: 'Durable',
      description: 'High-quality charging stations with long lifespan and warranty'
    }
  ] : [
    {
      icon: Zap,
      title: 'Schnelles Laden',
      description: 'Bis zu 10x schneller als normale Steckdose - voll geladen über Nacht'
    },
    {
      icon: Shield,
      title: 'Sicher & Zuverlässig',
      description: 'Integrierte Sicherheitsfunktionen und Überlastschutz für sorgenfreies Laden'
    },
    {
      icon: Euro,
      title: 'Kostenersparnis',
      description: 'Günstiger Nachtstrom und bis zu 900€ KfW-Förderung möglich'
    },
    {
      icon: Clock,
      title: 'Intelligentes Laden',
      description: 'Zeitgesteuert laden und von günstigen Stromtarifen profitieren'
    },
    {
      icon: Home,
      title: 'Komfort zu Hause',
      description: 'Laden Sie bequem in Ihrer eigenen Garage oder Carport'
    },
    {
      icon: Battery,
      title: 'Langlebig',
      description: 'Hochwertige Ladestationen mit langer Lebensdauer und Garantie'
    }
  ];

  return (
    <section id="vorteile" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Your ' : 'Ihre '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Benefits' : 'Vorteile'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'Why a wallbox is the best choice for your electric vehicle' : 'Warum eine Wallbox die beste Wahl für Ihr Elektrofahrzeug ist'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vorteile.map((vorteil, index) => {
            const Icon = vorteil.icon;
            return (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-sky-100 to-amber-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{vorteil.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{vorteil.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const WallboxModelle = () => {
  const { language } = useLanguage();
  
  const modelle = language === 'en' ? [
    {
      name: 'Basic Wallbox',
      leistung: '11 kW',
      beschreibung: 'Ideal for private use at home',
      features: [
        'Charging power up to 11 kW',
        'Fixed attached cable',
        'Integrated RCD',
        'Easy installation'
      ],
      preis: 'from €799',
      empfohlenText: 'Recommended'
    },
    {
      name: 'Smart Wallbox',
      leistung: '22 kW',
      beschreibung: 'Intelligent charging station with app control',
      features: [
        'Charging power up to 22 kW',
        'WiFi & app control',
        'Load management',
        'Energy management'
      ],
      preis: 'from €1,299',
      empfohlen: true,
      empfohlenText: 'Recommended'
    },
    {
      name: 'Business Wallbox',
      leistung: '22 kW',
      beschreibung: 'Professional solution for businesses',
      features: [
        'Charging power up to 22 kW',
        'RFID access control',
        'Billing system',
        'Backend integration'
      ],
      preis: 'from €1,899',
      empfohlenText: 'Recommended'
    }
  ] : [
    {
      name: 'Basic Wallbox',
      leistung: '11 kW',
      beschreibung: 'Ideal für den privaten Gebrauch zu Hause',
      features: [
        'Ladeleistung bis 11 kW',
        'Fest angeschlagenes Kabel',
        'FI-Schutzschalter integriert',
        'Einfache Installation'
      ],
      preis: 'ab 799€',
      empfohlenText: 'Empfohlen'
    },
    {
      name: 'Smart Wallbox',
      leistung: '22 kW',
      beschreibung: 'Intelligente Ladestation mit App-Steuerung',
      features: [
        'Ladeleistung bis 22 kW',
        'WLAN & App-Steuerung',
        'Lastmanagement',
        'Energiemanagement'
      ],
      preis: 'ab 1.299€',
      empfohlen: true,
      empfohlenText: 'Empfohlen'
    },
    {
      name: 'Business Wallbox',
      leistung: '22 kW',
      beschreibung: 'Professionelle Lösung für Unternehmen',
      features: [
        'Ladeleistung bis 22 kW',
        'RFID-Zugangskontrolle',
        'Abrechnungssystem',
        'Backend-Integration'
      ],
      preis: 'ab 1.899€',
      empfohlenText: 'Empfohlen'
    }
  ];

  const requestText = language === 'en' ? 'Request' : 'Anfragen';

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Our ' : 'Unsere '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Wallbox Models' : 'Wallbox-Modelle'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'The right charging station for every need' : 'Die passende Ladestation für jeden Bedarf'}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {modelle.map((modell, index) => (
            <Card key={index} className={`relative ${modell.empfohlen ? 'border-2 border-sky-600 shadow-2xl scale-105' : 'border-2 border-gray-100'} hover:shadow-xl transition-all duration-300`}>
              {modell.empfohlen && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-sky-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                    {modell.empfohlenText}
                  </span>
                </div>
              )}
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-sky-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Plug className="w-10 h-10 text-sky-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{modell.name}</h3>
                  <div className="text-sky-600 font-bold text-lg mb-2">{modell.leistung}</div>
                  <p className="text-gray-600 text-sm">{modell.beschreibung}</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {modell.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-sky-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="text-center pt-6 border-t border-gray-200">
                  <div className="text-2xl font-bold text-gray-900 mb-4">{modell.preis}</div>
                  <Button 
                    onClick={() => document.getElementById('kontakt').scrollIntoView({ behavior: 'smooth' })}
                    className={modell.empfohlen ? 'w-full bg-sky-600 hover:bg-sky-700' : 'w-full'}
                  >
                    {requestText}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const Installation = () => {
  const { language } = useLanguage();
  
  const schritte = language === 'en' ? [
    {
      nummer: 1,
      title: 'Consultation & Site Inspection',
      beschreibung: 'Free on-site visit and technical assessment'
    },
    {
      nummer: 2,
      title: 'Quote & Subsidy Application',
      beschreibung: 'Detailed quote and support with funding application'
    },
    {
      nummer: 3,
      title: 'Installation',
      beschreibung: 'Professional installation by certified electricians'
    },
    {
      nummer: 4,
      title: 'Commissioning',
      beschreibung: 'Instruction and testing of wallbox functions'
    }
  ] : [
    {
      nummer: 1,
      title: 'Beratung & Standortprüfung',
      beschreibung: 'Kostenlose Vor-Ort-Begehung und technische Prüfung'
    },
    {
      nummer: 2,
      title: 'Angebot & Förderantrag',
      beschreibung: 'Detailliertes Angebot und Unterstützung bei der Förderung'
    },
    {
      nummer: 3,
      title: 'Installation',
      beschreibung: 'Fachgerechte Installation durch zertifizierte Elektriker'
    },
    {
      nummer: 4,
      title: 'Inbetriebnahme',
      beschreibung: 'Einweisung und Test der Wallbox-Funktionen'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-sky-600 to-amber-500">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {language === 'en' ? 'Installation in 4 Easy Steps' : 'Installation in 4 einfachen Schritten'}
            </h2>
            <p className="text-xl text-white/90">
              {language === 'en' ? 'From planning to finished charging station' : 'Von der Planung bis zur fertigen Ladestation'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {schritte.map((schritt) => (
              <Card key={schritt.nummer} className="border-0 bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-2xl font-bold text-sky-600">{schritt.nummer}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{schritt.title}</h3>
                      <p className="text-white/90">{schritt.beschreibung}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => document.getElementById('kontakt').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg font-bold shadow-xl"
            >
              {language === 'en' ? 'Request Installation Now' : 'Jetzt Installation anfragen'}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const WallboxPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <WallboxHero />
      <WallboxVorteile />
      <WallboxModelle />
      <Installation />
      <Contact />
      <Footer />
    </div>
  );
};

export default WallboxPage;
